import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { History, Layers, RadioTower, Send, Trash2, Type as TypeIcon, X, AlertTriangle, Eraser } from 'lucide-react'
import { cgTemplatesApi } from '../../lib/api.js'
import { liveSourcesApi, testChannelApi, TEST_CASPAR_CHANNEL, type TestChannelEvent, type TestChannelSlot, type TestChannelState } from '../../lib/settingsApi.js'
import { useStore } from '../../store/index.js'
import { useTestChannelState } from '../../hooks/useTestChannelState.js'
import type { CgTemplate } from '../../types/index.js'
import { CgTemplateThumbnail } from '../cg/CgTemplateThumbnail.js'
import { TestChannelPlayer } from './TestChannelPlayer.js'
import '../cg/CgStudio.css'
import './TestChannelTab.css'


function isLogoTemplate(template: CgTemplate): boolean {
  return template.htmlPath.replace(/\\/g, '/').replace(/^\.\//, '').startsWith('trafficplay-custom/logos/')
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
}

function dayAndClock(iso: string): string {
  const date = new Date(iso)
  const today = new Date()
  const sameDay = date.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' }) === today.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })
  return sameDay ? clock(iso) : `${date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Paris' })} ${clock(iso)}`
}

const EVENT_LABEL: Record<string, string> = {
  CG_SENT:         'Habillage envoyé',
  CG_REMOVED:      'Habillage retiré',
  SOURCE_SENT:     'Source envoyée',
  SOURCE_STOPPED:  'Source arrêtée',
  SOURCE_REFUSED:  'Envoi refusé',
  SOURCE_RECONNECTING:        'Source figée, reconnexion auto',
  SOURCE_RECOVERED:           'Flux revenu',
  SOURCE_RECONNECT_ABANDONED: 'Reconnexion abandonnée',
  CHANNEL_CLEARED: 'Canal 2 vidé',
  CHANNEL_RESET:   'Canal 2 vidé hors TrafficPlay',
}

function eventIcon(kind: string) {
  if (kind.startsWith('CG_')) return <TypeIcon size={13} aria-hidden="true" />
  if (kind === 'SOURCE_REFUSED' || kind === 'CHANNEL_RESET' || kind === 'SOURCE_RECONNECTING' || kind === 'SOURCE_RECONNECT_ABANDONED') return <AlertTriangle size={13} aria-hidden="true" />
  if (kind.startsWith('SOURCE_')) return <RadioTower size={13} aria-hidden="true" />
  return <Eraser size={13} aria-hidden="true" />
}

/** Journal partagé : conservé sur le serveur, lisible depuis tous les postes. */
export const TestChannelJournal = memo(function TestChannelJournal({ events }: { events: TestChannelEvent[] }) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? events : events.slice(0, 6)
  return (
    <section className="tc-journal" aria-label="Journal du canal 2">
      <header className="tc-card__header">
        <History size={14} aria-hidden="true" />
        <h3>Journal du canal 2</h3>
        <span className="tc-caption">conservé sur le serveur · partagé entre tous les postes</span>
        {events.length > 6 && (
          <button type="button" className="tc-link tc-card__header-end" onClick={() => setExpanded(value => !value)}>
            {expanded ? 'Réduire' : 'Tout l’historique'}
          </button>
        )}
      </header>
      {events.length === 0
        ? <p className="tc-empty">Aucun envoi sur le canal 2 pour l’instant.</p>
        : (
          <ol className="tc-journal__list">
            {visible.map(event => (
              <li key={event.id} className={`tc-journal__row${event.kind === 'SOURCE_REFUSED' || event.kind === 'CHANNEL_RESET' ? ' is-warn' : ''}`}>
                <span className="tc-data tc-journal__time">{dayAndClock(event.createdAt)}</span>
                <span className="tc-journal__icon">{eventIcon(event.kind)}</span>
                <span className="tc-journal__what">
                  <strong>{EVENT_LABEL[event.kind] ?? event.kind}</strong>
                  <span className="tc-data">{[event.kind === 'CHANNEL_CLEARED' || event.kind === 'CHANNEL_RESET' ? null : event.label, event.detail].filter(Boolean).join(' · ')}</span>
                </span>
                <span className="tc-journal__who">{event.actorName ?? 'système'}</span>
              </li>
            ))}
          </ol>
        )}
    </section>
  )
})

/** « Sur le canal 2 » : ce qui y est empilé, avec retrait unitaire et vidage. */
export const TestChannelSlots = memo(function TestChannelSlots({ state, busy, onRemove, onClear }: {
  state:    TestChannelState | null
  busy:     string | null
  onRemove: (slot: TestChannelSlot) => void
  onClear:  () => void
}) {
  const slots = state?.slots ?? []
  // Couche vidéo en bas de pile, habillages au-dessus : ordre de l'image.
  const ordered = [...slots].sort((a, b) => (a.kind === 'SOURCE' ? 1 : 0) - (b.kind === 'SOURCE' ? 1 : 0))
  return (
    <section className="tc-card" aria-label="Sur le canal 2">
      <header className="tc-card__header">
        <Layers size={14} aria-hidden="true" />
        <h3>Sur le canal 2</h3>
        <span className="tc-data tc-card__header-end">{slots.length} élément{slots.length > 1 ? 's' : ''}</span>
      </header>
      {ordered.length === 0
        ? (
          <div className="tc-empty">
            <strong>Rien sur le canal 2</strong>
            <span>Les habillages et les sources envoyés s’empilent ici.</span>
          </div>
        )
        : (
          <ul className="tc-slots">
            {ordered.map(slot => (
              <li key={slot.slot} className="tc-slot">
                <span className="tc-slot__icon">{slot.kind === 'SOURCE' ? <RadioTower size={14} aria-hidden="true" /> : <TypeIcon size={14} aria-hidden="true" />}</span>
                <span className="tc-slot__what">
                  <strong>{slot.kind === 'SOURCE' ? 'Couche vidéo 1' : slot.detail?.split(' · ')[0] ?? 'Habillage'} · {slot.label}</strong>
                  <span className="tc-data">
                    {slot.kind === 'SOURCE' ? slot.detail : 'habillage enregistré'} · envoyé à {clock(slot.sentAt)}{slot.actorName ? ` · ${slot.actorName}` : ''}
                  </span>
                </span>
                <button type="button" className="tp-button" disabled={busy !== null} onClick={() => onRemove(slot)}>
                  <X size={13} aria-hidden="true" />{busy === slot.slot ? 'Retrait…' : 'Retirer'}
                </button>
              </li>
            ))}
          </ul>
        )}
      <footer className="tc-card__footer">
        <span className="tc-caption">{ordered.length ? 'L’empilement reste en place jusqu’à ce que vous le vidiez.' : 'Rien à vider pour l’instant.'}</span>
        <button type="button" className="tp-button" disabled={busy !== null || ordered.length === 0} onClick={onClear}>
          <Trash2 size={13} aria-hidden="true" />{busy === 'CLEAR' ? 'Vidage…' : 'Vider le canal 2'}
        </button>
      </footer>
    </section>
  )
})

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * Réglages › Canal de test : la réplique du canal 2, ce qui y est envoyé, les
 * habillages à essayer et le journal partagé. Le canal 1 n'est jamais visé.
 */
export const TestChannelTab = memo(function TestChannelTab({ channelId }: { channelId: string | null }) {
  const { state, setState, refresh } = useTestChannelState(channelId)
  const [templates, setTemplates] = useState<CgTemplate[]>([])
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    if (!channelId) return
    cgTemplatesApi.list(channelId).then(list => setTemplates(list.filter(template => template.active))).catch(() => setTemplates([]))
  }, [channelId])

  const toast = useCallback((message: string) => useStore.getState().pushToast(message, 'error'), [])

  const sentTemplateIds = useMemo(
    () => new Set((state?.slots ?? []).filter(slot => slot.kind === 'CG').map(slot => slot.refId)),
    [state],
  )
  const videoSlot = state?.slots.find(slot => slot.kind === 'SOURCE') ?? null
  const cgSlots = state?.slots.filter(slot => slot.kind === 'CG') ?? []
  const sourceLabel = videoSlot
    ? `${videoSlot.label}${videoSlot.detail ? ` · ${videoSlot.detail}` : ''}`
    : cgSlots.length ? 'habillage seul' : null

  const groups = useMemo(() => ([
    { title: 'Logos', items: templates.filter(isLogoTemplate) },
    { title: 'Autres habillages', items: templates.filter(template => !isLogoTemplate(template)) },
  ]).filter(group => group.items.length > 0), [templates])

  const toggleTemplate = useCallback(async (template: CgTemplate) => {
    if (!channelId) return
    setBusy(template.id)
    try {
      if (sentTemplateIds.has(template.id)) await cgTemplatesApi.hideTest(channelId, template.id)
      else await cgTemplatesApi.test(channelId, template.id)
    } catch (error) {
      toast(`Canal 2 : ${errorMessage(error)}`)
    } finally {
      setBusy(null)
      void refresh()
    }
  }, [channelId, sentTemplateIds, refresh, toast])

  const removeSlot = useCallback(async (slot: TestChannelSlot) => {
    if (!channelId) return
    setBusy(slot.slot)
    try {
      if (slot.kind === 'SOURCE') await liveSourcesApi.test(slot.refId, { previewChannel: TEST_CASPAR_CHANNEL, stop: true })
      else await cgTemplatesApi.hideTest(channelId, slot.refId)
    } catch (error) {
      toast(`Canal 2 : ${errorMessage(error)}`)
    } finally {
      setBusy(null)
      void refresh()
    }
  }, [channelId, refresh, toast])

  const clear = useCallback(async () => {
    if (!channelId) return
    setBusy('CLEAR')
    try {
      setState(await testChannelApi.clear(channelId))
    } catch (error) {
      toast(`Canal 2 : ${errorMessage(error)}`)
      void refresh()
    } finally {
      setBusy(null)
    }
  }, [channelId, refresh, setState, toast])

  return (
    <div className="settings-content--wide tc-tab">
      <div className="tc-tab__intro">
        <h2 className="settings-view-title">Canal de test</h2>
        <p className="settings-view-description">
          Essayez un habillage par-dessus une vraie image, sur le canal {TEST_CASPAR_CHANNEL} de CasparCG, avant de le prendre à l’antenne.
          Les sources live se testent dans l’onglet Sources live.
        </p>
        <span className={`tc-caspar-state${state && !state.observed ? ' is-warn' : ''}`}>
          <span className="tc-caspar-state__dot" aria-hidden="true" />
          {state === null ? 'Lecture de CasparCG…' : state.observed ? `CasparCG lu · canal ${state.casparChannel}` : 'CasparCG sans réponse · état enregistré'}
        </span>
      </div>

      <div className="tc-tab__layout">
        <div className="tc-tab__main">
          <TestChannelPlayer variant="full" occupied={(state?.slots.length ?? 0) > 0} sourceLabel={sourceLabel} />
          <TestChannelJournal events={state?.events ?? []} />
        </div>
        <div className="tc-tab__side">
          <TestChannelSlots state={state} busy={busy} onRemove={removeSlot} onClear={clear} />
          <section className="tc-card" aria-label="Habillages">
            <header className="tc-card__header">
              <TypeIcon size={14} aria-hidden="true" />
              <h3>Habillages</h3>
              <span className="tc-caption tc-card__header-end">version enregistrée, valeurs par défaut</span>
            </header>
            {groups.length === 0 && <p className="tc-empty">Aucun habillage actif sur cette chaîne.</p>}
            {groups.map(group => (
              <div key={group.title} className="tc-templates">
                <h4 className="tc-eyebrow tc-templates__title">{group.title}</h4>
                <ul>
                  {group.items.map(template => {
                    const sent = sentTemplateIds.has(template.id)
                    const slot = cgSlots.find(item => item.refId === template.id)
                    return (
                      <li key={template.id} className={`tc-template${sent ? ' is-sent' : ''}`}>
                        <span className="tc-template__thumb"><CgTemplateThumbnail template={template} /></span>
                        <span className="tc-template__what">
                          <strong>{template.name}</strong>
                          <span className="tc-data">
                            {template.cgLayer !== null ? `Layer CG ${template.cgLayer}` : 'Layer du logo'}
                            {sent && slot ? ` · sur le canal 2 depuis ${clock(slot.sentAt)}` : ''}
                          </span>
                        </span>
                        <button
                          type="button"
                          className={`tp-button${sent ? '' : ' tc-send'}`}
                          disabled={busy !== null}
                          onClick={() => void toggleTemplate(template)}
                        >
                          {sent ? <X size={13} aria-hidden="true" /> : <Send size={13} aria-hidden="true" />}
                          {busy === template.id ? (sent ? 'Retrait…' : 'Envoi…') : sent ? 'Retirer' : 'Envoyer'}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  )
})
